import React, { useEffect, useState, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface SendReminderModalProps {
  open: boolean;
  vendorInvoiceId: number | string;
  selectedLineItemIds: number[];
  onClose: () => void;
  onSent: () => void;
}

const SendReminderModal: React.FC<SendReminderModalProps> = ({
  open,
  vendorInvoiceId,
  selectedLineItemIds,
  onClose,
  onSent,
}) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [resolvedLineItemIds, setResolvedLineItemIds] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchPreview = async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (selectedLineItemIds.length > 0) params.line_item_ids = selectedLineItemIds;
        const response: any = await axios.get(`/vendors/vendor_invoices/${vendorInvoiceId}/reminder_preview`, {
          params,
        });
        if (cancelled) return;
        setRecipientsInput((response.data?.recipients || []).join(', '));
        setSubject(response.data?.subject || '');
        setBody(response.data?.body || '');
        setResolvedLineItemIds(response.data?.line_item_ids || []);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to load reminder preview';
        showErrorSnackBar(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPreview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendorInvoiceId, JSON.stringify(selectedLineItemIds)]);

  const handleSend = async () => {
    const recipients = recipientsInput
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      showErrorSnackBar('At least one recipient email is required');
      return;
    }

    setSending(true);
    try {
      await axios.post(`/vendors/vendor_invoices/${vendorInvoiceId}/send_reminder`, {
        recipients,
        subject,
        body,
        line_item_ids: resolvedLineItemIds,
      });
      showSuccessSnackBar('Reminder sent');
      onSent();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to send reminder';
      showErrorSnackBar(errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send Reminder</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Line items included
              </Typography>
              <Box>
                <Chip label={`${resolvedLineItemIds.length} line items`} size="small" />
              </Box>
            </Box>
            <TextField
              label="To"
              helperText="Comma-separated email addresses. Defaults to the vendor's Accounts Payable - Statements contact."
              value={recipientsInput}
              onChange={(e) => setRecipientsInput(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              multiline
              minRows={12}
              size="small"
              InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={loading || sending || resolvedLineItemIds.length === 0}
        >
          {sending ? 'Sending…' : 'Send Reminder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendReminderModal;
